import React, { useState, useEffect } from 'react';
import { autoJsStoreUtils, autoWebViewJs } from '@droid/android';

interface ErrorInfo {
  id: string;
  timestamp: number;
  errorType: string;
  severity: string;
  message: string;
  stackTrace?: string;
  context?: Record<string, string>;
}

interface ErrorStorage {
  errors: ErrorInfo[];
}

export const ErrorDisplay: React.FC = () => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  const loadErrors = () => {
    try {
      const parsed = autoJsStoreUtils.read<ErrorStorage>('EMERGENCY_ERRORS_STORAGE', {"errors":[]});
      // const parsed: ErrorStorage = JSON.parse(errorData);
      
      if (parsed && parsed.errors && parsed.errors.length > 0) {
        setErrors(parsed.errors);
        setIsVisible(true);
      } else {
        setErrors([]);
        setIsVisible(false);
      }
    } catch (e) {
      console.error('读取错误信息失败:', e);
      setErrors([]);
      setIsVisible(false);
    }
  };

  const clearErrors = () => {
    try {
      // 清除本地存储
      autoJsStoreUtils.write('EMERGENCY_ERRORS_STORAGE', '{"errors":[]}');
      setErrors([]);
      setIsVisible(false);
      
      console.log('错误信息已清除');
    } catch (e) {
      console.error('清除错误信息失败:', e);
    }
  };

  const handleErrorEvent = () => {
    console.log('收到错误事件，重新加载错误列表');
    loadErrors();
  };

  const handleErrorClearedEvent = () => {
    console.log('收到错误清除事件');
    setErrors([]);
    setIsVisible(false);
  };

  useEffect(() => {
    // 页面加载时检查错误
    loadErrors();

    // 监听错误事件
    const handleFocus = () => {
      console.log('窗口获得焦点，检查错误信息');
      loadErrors();
    };

    // 监听自定义事件
    const handleCustomError = (event: any) => {
      console.log('收到自定义错误事件:', event);
      handleErrorEvent();
    };

    const handleCustomErrorCleared = (event: any) => {
      console.log('收到自定义错误清除事件:', event);
      handleErrorClearedEvent();
    };

    // 添加事件监听器
    window.addEventListener('focus', handleFocus);
    window.addEventListener('emergency_error_added', handleCustomError);
    window.addEventListener('emergency_errors_cleared', handleCustomErrorCleared);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('emergency_error_added', handleCustomError);
      window.removeEventListener('emergency_errors_cleared', handleCustomErrorCleared);
    };
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-500 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-500 text-orange-800';
      case 'medium': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'low': return 'bg-blue-100 border-blue-500 text-blue-800';
      default: return 'bg-gray-100 border-gray-500 text-gray-800';
    }
  };

  const getSeverityText = (severity: string) => {
    switch (severity) {
      case 'critical': return '严重';
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return '未知';
    }
  };

  const getErrorTypeText = (type: string) => {
    switch (type) {
      case 'network_error': return '网络错误';
      case 'api_error': return 'API错误';
      case 'system_error': return '系统错误';
      default: return type;
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  if (!isVisible || errors.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 z-50 max-w-2xl mx-auto">
      <div className="bg-white border border-red-300 rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-red-800 flex items-center">
            <span className="mr-2">⚠️</span>
            发现 {errors.length} 个错误
          </h3>
          <button
            onClick={clearErrors}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            我已知道
          </button>
        </div>
        
        <div className="max-h-64 overflow-y-auto space-y-2">
          {errors.slice(0, 3).map((error) => (
            <div
              key={error.id}
              className={`p-3 rounded border-l-4 ${getSeverityColor(error.severity)}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">
                  {getErrorTypeText(error.errorType)}
                </span>
                <span className="text-xs opacity-75">
                  {getSeverityText(error.severity)} | {formatTime(error.timestamp)}
                </span>
              </div>
              
              <p className="text-sm mb-2">{error.message}</p>
              
              {error.context && Object.keys(error.context).length > 0 && (
                <div className="text-xs opacity-75">
                  <strong>上下文:</strong>
                  {Object.entries(error.context).map(([key, value]) => (
                    <span key={key} className="ml-2">
                      {key}: {value}
                    </span>
                  ))}
                </div>
              )}
              
              {error.stackTrace && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer opacity-75">
                    查看详细错误信息
                  </summary>
                  <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-x-auto whitespace-pre-wrap">
                    {error.stackTrace}
                  </pre>
                </details>
              )}
            </div>
          ))}
          
          {errors.length > 3 && (
            <div className="text-center text-sm text-gray-600 py-2">
              还有 {errors.length - 3} 个错误未显示...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
